import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { LOGO_URL, RUBHAAI_LOGO_URL } from "../lib/site";

const HIGHLIGHTS = [
  "Baraat & Sangeet lift",
  "Sufi-led evenings",
  "Ceremony scoring",
  "Delhi to destination weddings",
];

export const PalaceHeroLite = () => {
  return (
    <section
      id="hero"
      data-testid="hero-lite-section"
      className="relative overflow-hidden bg-[#080502]"
      style={{ minHeight: "100svh" }}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(212,175,55,0.16),transparent_36%),radial-gradient(circle_at_18%_78%,rgba(130,58,22,0.18),transparent_28%),radial-gradient(circle_at_84%_70%,rgba(212,175,55,0.10),transparent_26%)]" />
        <div
          className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #d4af37 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      <div className="relative z-10 flex min-h-[100svh] flex-col items-center justify-center px-6 pb-16 pt-24 text-center">
        <motion.img
          src={LOGO_URL}
          alt="AB Events"
          initial={{ opacity: 0, y: 14, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-5 h-auto w-24 object-contain drop-shadow-[0_0_40px_rgba(212,175,55,0.55)]"
          draggable={false}
        />

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="font-serif text-[36px] font-light uppercase leading-none text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)] sm:text-5xl"
          style={{ letterSpacing: "0.24em" }}
        >
          AB&nbsp;Events
        </motion.h1>

        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="my-6 h-px w-16 origin-center bg-[#d4af37] shadow-[0_0_10px_rgba(212,175,55,0.5)]"
        />

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.25 }}
          className="text-[10px] uppercase italic tracking-[0.45em] text-[#d4af37] sm:text-xs"
        >
          A Legacy of Indian Wedding Grandeur
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ duration: 0.85, delay: 0.32 }}
          className="mt-5 max-w-xl text-sm font-light leading-relaxed text-white/72"
        >
          A lighter, faster mobile stage that keeps the luxury atmosphere without
          the heavier 3D load.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-2"
        >
          {HIGHLIGHTS.map((item) => (
            <div
              key={item}
              className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-[10px] uppercase tracking-[0.24em] text-white/72"
            >
              {item}
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.95, delay: 0.5 }}
          className="mt-8 w-[min(88vw,420px)]"
        >
          <div className="relative">
            <div className="absolute inset-x-[16%] top-[12%] h-[34%] rounded-full bg-[#d4af37]/14 blur-3xl" />
            <div className="absolute inset-x-[18%] bottom-[8%] h-[18%] rounded-full bg-black/70 blur-2xl" />
            <img
              src={RUBHAAI_LOGO_URL}
              alt="Rubai The Sufi"
              data-testid="hero-rubhaai-logo"
              className="relative z-10 mx-auto h-auto w-full select-none object-contain drop-shadow-[0_20px_48px_rgba(0,0,0,0.5)]"
              draggable={false}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.65 }}
          className="mt-10 flex w-full max-w-[340px] flex-col items-center gap-4 sm:max-w-none sm:flex-row"
        >
          <a
            href="#bookings"
            data-testid="hero-book-cta"
            className="gold-button inline-flex w-full items-center justify-center gap-2 rounded-sm px-8 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] sm:w-auto sm:text-xs sm:tracking-[0.3em]"
          >
            Book Now <ArrowRight size={14} />
          </a>
          <a
            href="#services"
            data-testid="hero-explore-cta"
            className="rounded-sm border border-white/40 bg-white/5 px-8 py-4 text-center text-[11px] font-medium uppercase leading-relaxed tracking-[0.2em] text-white backdrop-blur-md transition-all duration-400 hover:border-white hover:bg-white/15 sm:text-xs sm:tracking-[0.3em]"
          >
            Explore the Collection
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 flex flex-col items-center gap-2 sm:hidden"
        >
          <div className="text-center text-[9px] uppercase tracking-[0.34em] text-white/68">
            Scroll to step inside
          </div>
          <ChevronDown size={20} className="animate-bounce text-[#d4af37]" />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.8 }}
        className="pointer-events-none absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 sm:flex"
      >
        <div className="text-[10px] uppercase tracking-[0.45em] text-white/68">
          Scroll to step inside
        </div>
        <ChevronDown size={20} className="animate-bounce text-[#d4af37]" />
      </motion.div>
    </section>
  );
};

export default PalaceHeroLite;
